import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { fmtMoney, fmtDate, vehicleLabel } from "@/lib/format";
import { CheckCircle, Clock, ArrowCounterClockwise, WarningCircle, Plus, Storefront } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import BranchFilter from "@/components/BranchFilter";

const STATUS_META = {
  pending: { icon: Clock, label: "Pending", cls: "border-destructive bg-destructive/5 text-destructive" },
  short: { icon: WarningCircle, label: "Short", cls: "border-[#E08600] bg-[#FFF6E5] text-[#A35700]" },
  received: { icon: CheckCircle, label: "Received", cls: "border-[#00B341] bg-[#00B341]/10 text-[#00B341]" },
};

function StatusPill({ status, testId }) {
  const meta = STATUS_META[status] || STATUS_META.pending;
  const Icon = meta.icon;
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center gap-1 px-2 py-0.5 border ${meta.cls} text-[10px] font-mono uppercase tracking-wider`}
    >
      <Icon size={10} weight="bold" /> {meta.label}
    </span>
  );
}

function StatPill({ label, value, accent, testId }) {
  return (
    <div className={`border p-4 ${accent}`} data-testid={testId}>
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] opacity-80">{label}</div>
      <div className="font-display text-2xl font-bold mt-2 tabular-nums">{value}</div>
    </div>
  );
}

function ReceiveDialog({ row, onClose, onSaved }) {
  const expected = row.expected_amount;
  const already = row.amount_received || 0;
  const outstanding = Math.max(expected - already, 0);
  const [amount, setAmount] = useState(outstanding > 0 ? String(outstanding) : "");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [ref, setRef] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) {
      toast.error("Enter a positive amount.");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/accounts/platform-receivables/${row.sale_id}/receive?source=${row.source || "sale"}`, {
        amount: amt,
        received_on: date,
        bank_reference: ref,
      });
      toast.success(amt >= outstanding ? "Marked received" : "Tranche booked");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const isTopUp = already > 0;
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        data-testid="platform-receive-dialog"
        className="bg-canvas-card border-2 border-ink p-6 w-full max-w-md space-y-4"
      >
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-muted">
            Platform Receivable {isTopUp ? "· Tranche" : ""}
          </div>
          <h3 className="font-display text-lg font-semibold text-ink mt-1">
            {row.platform_label} · {fmtMoney(expected)}
          </h3>
          <div className="font-mono text-[11px] text-ink-subtle mt-1">
            {row.customer?.name || "—"} · Already settled: {fmtMoney(already)} · Outstanding: {fmtMoney(outstanding)}
          </div>
        </div>
        <div>
          <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">
            Amount Received Now (₹)
          </label>
          <input
            type="number"
            step="0.01"
            min="0.01"
            required
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            data-testid="platform-receive-amount"
            className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
          />
          <div className="font-mono text-[10px] text-ink-subtle mt-1">
            Enter less than {fmtMoney(outstanding)} to leave a SHORT balance.
          </div>
        </div>
        <div>
          <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">
            Received On
          </label>
          <input
            type="date"
            required
            value={date}
            onChange={(e) => setDate(e.target.value)}
            data-testid="platform-receive-date"
            className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
          />
        </div>
        <div>
          <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">
            Bank Reference / Settlement UTR
          </label>
          <input
            value={ref}
            onChange={(e) => setRef(e.target.value)}
            placeholder="Platform settlement UTR / NEFT id"
            data-testid="platform-receive-ref"
            className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
          />
        </div>
        {(row.receipts || []).length > 0 && (
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">
              Previous tranches ({row.receipts.length})
            </label>
            <ul className="border border-canvas-line max-h-32 overflow-y-auto">
              {row.receipts.map((rc, idx) => (
                <li
                  key={idx}
                  data-testid={`platform-receipt-${row.sale_id}-${idx}`}
                  className="px-2 py-1 text-[11px] font-mono border-b border-canvas-line last:border-b-0 flex justify-between gap-2"
                >
                  <span>{fmtDate(rc.received_on)} · {rc.bank_reference || "—"}</span>
                  <span className="text-[#00713a] tabular-nums font-semibold">{fmtMoney(rc.amount)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 border border-canvas-line text-xs hover:border-ink"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="platform-confirm-received"
            className="px-3 py-1.5 bg-ink text-white text-xs font-semibold hover:bg-brand"
          >
            {saving ? "Saving…" : isTopUp ? "Book Tranche" : "Mark Received"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function PlatformReceivablesTab() {
  const { user } = useAuth();
  const canReconcile = (user?.permissions || []).includes("accounts.reconcile");
  const [data, setData] = useState({ rows: [], totals: {} });
  const [statusFilter, setStatusFilter] = useState("pending");
  const [activeRow, setActiveRow] = useState(null);
  const [branchId, setBranchId] = useState("");

  const load = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      if (branchId) params.branch_id = branchId;
      const { data: res } = await api.get("/accounts/platform-receivables", { params });
      setData(res);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, [statusFilter, branchId]);

  useEffect(() => {
    load();
  }, [load]);

  const revert = async (saleId, source) => {
    if (!window.confirm("Reset this platform receivable to pending and clear all booked tranches?")) return;
    try {
      await api.post(`/accounts/platform-receivables/${saleId}/revert?source=${source || "sale"}`);
      toast.success("Reverted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const t = data.totals || {};
  return (
    <div className="space-y-4" data-testid="platform-receivables-tab">
      <div className="flex items-end gap-3 flex-wrap">
        <BranchFilter value={branchId} onChange={setBranchId} data-testid="platform-branch-filter" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatPill
          label={`Pending YTD (${t.ytd_pending_count || 0})`}
          value={fmtMoney(t.ytd_pending_amount || 0)}
          accent="border-destructive bg-destructive/5 text-ink"
          testId="platform-ytd-pending"
        />
        <StatPill
          label={`Short YTD (${t.ytd_short_count || 0})`}
          value={fmtMoney(t.ytd_short_amount || 0)}
          accent="border-[#E08600] bg-[#FFF6E5] text-ink"
          testId="platform-ytd-short"
        />
        <StatPill
          label={`Received YTD (${t.ytd_received_count || 0})`}
          value={fmtMoney(t.ytd_received_amount || 0)}
          accent="border-[#00B341] bg-[#00B341]/5 text-ink"
          testId="platform-ytd-received"
        />
        <StatPill
          label="All-Time Outstanding"
          value={fmtMoney((t.pending_amount || 0) + (t.short_amount || 0))}
          accent="border-canvas-line bg-canvas-card text-ink"
          testId="platform-outstanding-total"
        />
      </div>

      {data.fy_window && (
        <div className="font-mono text-[10px] text-ink-subtle uppercase tracking-wider">
          YTD window: {data.fy_window.from} → {data.fy_window.to}
        </div>
      )}

      <div className="flex border border-canvas-line w-fit">
        {[
          { v: "pending", l: "Pending" },
          { v: "short", l: "Short" },
          { v: "received", l: "Received" },
          { v: "all", l: "All" },
        ].map((opt) => (
          <button
            key={opt.v}
            onClick={() => setStatusFilter(opt.v)}
            data-testid={`platform-filter-${opt.v}`}
            className={`px-4 py-2 text-xs font-mono uppercase tracking-wider ${
              statusFilter === opt.v ? "bg-ink text-white" : "text-ink hover:bg-canvas-panel"
            }`}
          >
            {opt.l}
          </button>
        ))}
      </div>

      <div className="bg-canvas-card border border-canvas-line overflow-x-auto">
        <table className="w-full text-sm" data-testid="platform-table">
          <thead className="border-b border-canvas-line bg-canvas-panel">
            <tr>
              {[
                { l: "Branch" }, { l: "Date" }, { l: "Customer" }, { l: "Vehicle / Invoice" }, { l: "Platform / Insurer" },
                { l: "Expected ₹", r: true }, { l: "Received ₹", r: true }, { l: "Short ₹", r: true },
                { l: "Status" }, { l: "Last on" }, { l: "Settlement UTR" }, { l: "", r: true },
              ].map((h, i) => (
                <th
                  key={`${h.l}-${i}`}
                  className={`px-3 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted ${h.r ? "text-right" : "text-left"}`}
                >
                  {h.l}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.rows.length === 0 && (
              <tr>
                <td colSpan={12} className="px-3 py-10 text-center font-mono text-xs text-ink-subtle">
                  No platform receivables. {statusFilter !== "all" && `Try "All" filter.`}
                </td>
              </tr>
            )}
            {data.rows.map((r) => {
              const last = (r.receipts || []).slice(-1)[0];
              return (
                <tr key={r.sale_id} data-testid={`platform-row-${r.sale_id}`} className="border-b border-canvas-line last:border-b-0">
                  <td className="px-3 py-3 font-mono text-[10px] uppercase tracking-wider">{r.branch_name || "—"}</td>
                  <td className="px-3 py-3 font-mono text-[11px]">{fmtDate(r.sale_date)}</td>
                  <td className="px-3 py-3">{r.customer?.name || "—"}</td>
                  <td className="px-3 py-3 text-xs">
                    {r.source === "service" && (
                      <span className="inline-block mb-0.5 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wider border border-[#EA580C] text-[#EA580C]" data-testid={`platform-source-${r.sale_id}`}>
                        Service · Cashless
                      </span>
                    )}
                    <div>{r.vehicle ? vehicleLabel(r.vehicle) : r.vehicle_text || "—"}</div>
                    {r.vehicle?.vin && (
                      <div className="font-mono text-[10px] text-ink-subtle">{r.vehicle.vin}</div>
                    )}
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <Storefront size={11} weight="bold" className="text-ink-subtle shrink-0" />
                      {(r.platforms || []).map((p, idx) => (
                        <span
                          key={idx}
                          className="font-mono text-[10px] px-1.5 py-0.5 border border-canvas-line bg-canvas-panel"
                        >
                          {p.platform_name} · {fmtMoney(p.amount)}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-3 py-3 text-right font-mono tabular-nums">{fmtMoney(r.expected_amount)}</td>
                  <td className="px-3 py-3 text-right font-mono tabular-nums text-[#00713a]">
                    {r.amount_received > 0 ? fmtMoney(r.amount_received) : "—"}
                  </td>
                  <td className="px-3 py-3 text-right font-mono tabular-nums text-[#A35700]">
                    {r.short_amount > 0 ? fmtMoney(r.short_amount) : "—"}
                  </td>
                  <td className="px-3 py-3">
                    <StatusPill status={r.status} testId={`platform-status-${r.sale_id}`} />
                  </td>
                  <td className="px-3 py-3 font-mono text-[11px]">{last?.received_on ? fmtDate(last.received_on) : "—"}</td>
                  <td className="px-3 py-3 font-mono text-[11px]">{last?.bank_reference || "—"}</td>
                  <td className="px-3 py-3 text-right">
                    {r.status === "received" ? (
                      canReconcile ? (
                        <button
                          onClick={() => revert(r.sale_id, r.source)}
                          data-testid={`platform-revert-${r.sale_id}`}
                          className="p-1.5 border border-canvas-line hover:border-ink"
                          title="Reset to pending"
                        >
                          <ArrowCounterClockwise size={13} weight="bold" />
                        </button>
                      ) : (
                        <span className="text-[10px] font-mono text-ink-subtle uppercase tracking-wider">received</span>
                      )
                    ) : canReconcile ? (
                      <button
                        onClick={() => setActiveRow(r)}
                        data-testid={`platform-receive-${r.sale_id}`}
                        className="px-2.5 py-1.5 bg-ink text-white text-xs font-semibold hover:bg-brand inline-flex items-center gap-1"
                      >
                        {r.status === "short" ? <><Plus size={11} weight="bold" /> Tranche</> : "Mark received"}
                      </button>
                    ) : (
                      <span
                        className="text-[10px] font-mono text-ink-subtle uppercase tracking-wider"
                        title="Only the Accounts team can record receipts. Contact accounts to reconcile this line."
                        data-testid={`platform-locked-${r.sale_id}`}
                      >
                        accounts-only
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {activeRow && (
        <ReceiveDialog
          row={activeRow}
          onClose={() => setActiveRow(null)}
          onSaved={() => {
            setActiveRow(null);
            load();
          }}
        />
      )}
    </div>
  );
}
